import * as React from 'react';
import {FC} from 'react';
import {BooleanInput, Edit, EditProps, SelectArrayInput, SelectInput, SimpleForm, TextInput, TranslatableInputs, useTranslate} from 'react-admin';

import Title from '../../components/list/Title';
import {listEditCreate} from "../../styles/listEditCreate";
import {filterProps} from "../../functions/common";
import cfgLocales from '../../config/locale';
import {Box, Typography} from "@material-ui/core";
import RichTextInput from "ra-input-rich-text";
import InfoPopup from "../../components/global/InfoPopup";
import {
    transformVisitorCountryPayload,
    validateCatalogReferenceFields,
} from "./catalogReference";

const VisitorCountryEdit: FC<EditProps> = props => {
    const classes = listEditCreate();
    const newProps = filterProps(props);
    const translate = useTranslate();

    const validate = (values: Record<string, unknown>) =>
        validateCatalogReferenceFields(
            values,
            translate('resources.admin/visitor-countries.validation.catalog_reference_all_required')
        );

    return (
        <Edit
            {...newProps}
            undoable={false}
            transform={(data) => transformVisitorCountryPayload(data) as typeof data}
        >
            <SimpleForm className={classes.sidebar} validate={validate}>
                <Title source="code" label="resources.admin/visitor-countries.titles.edit"/>
                <TranslatableInputs locales={Object.keys(cfgLocales.languageOptions)} defaultLocale={cfgLocales.defaultLocale}>
                    <TextInput source="name" fullWidth={true}/>
                    <TextInput source="shipping_info" fullWidth={true} multiline={true} rows={2} />
                    <TextInput source="shipping_info_hover_text" fullWidth={true} multiline={true} rows={2} />
                    <RichTextInput source="legal_texts" />
                    <RichTextInput source="special_terms_link" />
                </TranslatableInputs>
                <TextInput fullWidth={true} source='terms_link' label={'resources.admin/visitor-countries.fields.terms_link'}/>
                <>
                    <Typography variant='h6' className={classes.title}>
                        {translate("resources.admin/visitor-countries.titles.terms_settings")}
                    </Typography>
                    <BooleanInput source='is_special_terms_required' label={'resources.admin/visitor-countries.fields.is_special_terms_required'} />
                    <BooleanInput source='is_terms_required' label={'resources.admin/visitor-countries.fields.is_terms_required'}/>
                </>
                <TextInput source='code' label={'resources.admin/visitor-countries.fields.code'}/>
                <TextInput source='vkorg' label={'resources.admin/visitor-countries.fields.vkorg'}/>
                <SelectArrayInput
                    source="payment_type"
                    choices={[
                        {id: 'invoice', name: translate('resources.admin/visitor-countries.paymentOptions.invoice')},
                        {id: 'unzer', name: translate('resources.admin/visitor-countries.paymentOptions.unzer')}
                    ]}/>
                <SelectInput source="default_language" choices={Object.keys(cfgLocales.languageOptions).map(el => ({id: el, name: el}))}/>
                <SelectInput
                    source="status"
                    choices={[
                        {id: 'active', name: translate('resources.admin/visitor-countries.statusOptions.active')},
                        {id: 'inactive', name: translate('resources.admin/visitor-countries.statusOptions.inactive')}
                    ]}/>
                <>
                    <Typography variant='h6' className={classes.title}>
                        {translate("resources.admin/visitor-countries.titles.catalog")}
                    </Typography>
                </>
                <BooleanInput source='is_catalog_only' label={'resources.admin/visitor-countries.fields.is_catalog_only'}/>
                <TextInput
                    source='catalog_price_group'
                    label={'resources.admin/visitor-countries.fields.catalog_price_group'}
                    helperText={translate('resources.admin/visitor-countries.info.catalog_price_group')}
                    fullWidth
                />
                <>
                    <Box display="flex" alignItems="center" className={classes.title}>
                        <Typography variant='h6'>
                            {translate("resources.admin/visitor-countries.titles.reference_customer")}
                        </Typography>
                        <InfoPopup text={translate('resources.admin/visitor-countries.info.reference_customer')}/>
                    </Box>
                    <TextInput
                        source='catalog_reference_kunnr'
                        label={'resources.admin/visitor-countries.fields.catalog_reference_kunnr'}
                        fullWidth
                    />
                    <TextInput
                        source='catalog_reference_parnr'
                        label={'resources.admin/visitor-countries.fields.catalog_reference_parnr'}
                        fullWidth
                    />
                    <TextInput
                        source='catalog_reference_vkorg'
                        label={'resources.admin/visitor-countries.fields.catalog_reference_vkorg'}
                        fullWidth
                    />
                </>
                <BooleanInput source='catalog_show_prices' label={'resources.admin/visitor-countries.fields.catalog_show_prices'}/>
                <TextInput source='catalog_currency' label={'resources.admin/visitor-countries.fields.catalog_currency'}/>
                <TextInput source='catalog_delivery_price' label={'resources.admin/visitor-countries.fields.catalog_delivery_price'}/>
                <TextInput source='catalog_vat_percentage' label={'resources.admin/visitor-countries.fields.catalog_vat_percentage'}/>
                <>
                    <Typography variant='h6' className={classes.title}>
                        {translate("resources.admin/visitor-countries.titles.murr_contact_info")}
                    </Typography>
                    <TextInput fullWidth source='murr_contact_info.company_name' label={'resources.admin/visitor-countries.fields.murr_contact_info.company_name'}/>
                    <TextInput fullWidth source='murr_contact_info.address_line_1' label={'resources.admin/visitor-countries.fields.murr_contact_info.address_line_1'}/>
                    <TextInput fullWidth source='murr_contact_info.address_line_2' label={'resources.admin/visitor-countries.fields.murr_contact_info.address_line_2'}/>
                    <TextInput fullWidth source='murr_contact_info.country' label={'resources.admin/visitor-countries.fields.murr_contact_info.country'}/>
                    <TextInput fullWidth source='murr_contact_info.phone' label={'resources.admin/visitor-countries.fields.murr_contact_info.phone'}/>
                    <TextInput fullWidth source='murr_contact_info.fax' label={'resources.admin/visitor-countries.fields.murr_contact_info.fax'}/>
                    <TextInput fullWidth source='murr_contact_info.url' label={'resources.admin/visitor-countries.fields.murr_contact_info.url'}/>
                </>
            </SimpleForm>
        </Edit>
    );
};

export default VisitorCountryEdit;
